import { useEffect, useState } from "react";
import {Link, useNavigate, useParams} from "react-router-dom";
import {API_BASE_URL, useApiClient} from "../apiClient";
import {useAuth} from "../auth/AuthContext";

export default function BookDetailsPage() {
    const {id} = useParams();
    const navigate = useNavigate();
    const {authorizedFetch} = useApiClient();
    const {token, logout} = useAuth();
    const [book, setBook] = useState(null);
    const [status, setStatus] = useState({loading: true, error: ""});

    useEffect(() => {
        const loadBook = async () => {
            try {
                const data = await authorizedFetch(`/books/${id}`);
                setBook(data);
                setStatus({loading: false, error: ""});
            } catch (err) {
                setStatus({
                    loading: false,
                    error: err.message || "Failed to load book details",
                });
            }
        };
        loadBook();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- authorizedFetch identity is stable for this screen
    }, [id]);

    const handleDownload = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/books/${id}/download`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/octet-stream',
                },
            });

            if (response.status === 401) {
                logout();
                navigate("/login", {replace: true});
                return;
            }

            if (!response.ok) {
                throw new Error(`Download failed: ${response.status}`);
            }

            // Get the filename from Content-Disposition header if available
            const contentDisposition = response.headers.get('Content-Disposition');
            let filename = book.filename || `book-${id}`;
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            }

            // Create blob and download
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (err) {
            alert(err.message || 'Failed to download book');
        }
    };

    if (status.loading) {
        return (
            <section className="page">
                <p>Loading book details...</p>
            </section>
        );
    }

    if (status.error) {
        return (
            <section className="page">
                <div className="alert alert-error">{status.error}</div>
                <Link to="/search" className="btn btn-secondary">
                    Back to search
                </Link>
            </section>
        );
    }

    if (!book) {
        return (
            <section className="page">
                <p>Book not found.</p>
                <Link to="/search" className="btn btn-secondary">
                    Back to search
                </Link>
            </section>
        );
    }

    return (
        <section className="page">
            <Link to="/search" className="back-link">
                ← Back to search
            </Link>

            <h1>{book.title}</h1>
            <p className="book-publisher">
                <strong>Publisher:</strong> {book.publisher}
            </p>

            <p className="book-filename">
                <strong>Filename:</strong> {book.filename}
            </p>

            {book.published && (
                <p className="muted">
                    Added to library: {new Date(book.published).toLocaleString()}
                </p>
            )}

            <button onClick={handleDownload} className="btn btn-primary" style={{marginBottom: '1rem'}}>
                Download Book
            </button>
            {book.description && (
                <>
                    <h2>About this book</h2>
                    <p className="book-description-long">{book.description}</p>
                </>
            )}
        </section>
    );
}